import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { ANNOTATIONS_BASE_QUERY_KEY, createAnnotationAssignment } from 'api/annotationAssignments';
import { BarElement, CategoryScale, Chart, ChartData, ChartOptions, Legend, LinearScale, Tooltip } from 'chart.js';
import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import SlideLinkWithPreview from 'components/atoms/SlideLinkWithPreview';
import { AnnotationAssignment } from 'interfaces/annotation';
import { Permission } from 'interfaces/permissionOption';
import { enqueueSnackbar } from 'notistack';
import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Link as RouterLink } from 'react-router-dom';
import {
  AnnotationWorkingStatesIcons,
  getAnnotationWorkingStateColor,
  getAnnotationWorkingStateLabel,
} from 'services/annotations/workingStates';
import { buildSlideViewerUrl } from 'utils/helpers';
import { getQueryErrorMessage } from 'utils/httpError';
import { useAnnotationAssignments } from 'utils/useAnnotationAssignments';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { usePermissions } from 'utils/usePermissions';

import {
  ANALYSIS_AREAS_ASSIGNMENT_NAME,
  AnalysisAreaSlide,
  analysisAreaTodo,
  getAnalysisAreaSlideCounts,
  getAnalysisAreasDatasetUrl,
  isAnalysisAreaAssignment,
} from './analysisAreas';
import { ANALYSIS_AREAS_REVIEW_TAB } from './analysisAreasExplore';

interface AnalysisAreasDialogProps {
  studyId: string;
  labId?: string;
  slides: AnalysisAreaSlide[];
  onClose: () => void;
}

const SLIDES_PER_SUMMARY_PAGE = 10;

Chart.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const AssignmentStatusSummary = ({
  assignment,
  slides,
  studyId,
  labId,
}: {
  assignment: AnnotationAssignment;
  slides: AnalysisAreaSlide[];
  studyId: string;
  labId?: string;
}) => {
  const [page, setPage] = useState(0);
  const theme = useTheme();
  const annotationSlideIds = new Set((assignment.annotations || []).map((annotation) => annotation.slideId));
  const rows = [
    ...slides,
    ...(assignment.annotations || [])
      .filter((annotation) => !slides.some((slide) => slide.id === annotation.slideId))
      .map((annotation) => ({ id: annotation.slideId, label: annotation.slideId })),
  ];
  const slideCounts = rows.map((slide) => {
    const counts = getAnalysisAreaSlideCounts(assignment, slide.id);
    const annotation = (assignment.annotations || []).find((item) => item.slideId === slide.id);
    return {
      slideId: slide.id,
      label: slide.label || slide.id,
      include: counts.include.saved + counts.include.draft,
      exclude: counts.exclude.saved + counts.exclude.draft,
      workingState: annotation?.workingState,
    };
  });
  const pageCount = Math.max(1, Math.ceil(slideCounts.length / SLIDES_PER_SUMMARY_PAGE));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleSlideCounts = slideCounts.slice(
    currentPage * SLIDES_PER_SUMMARY_PAGE,
    (currentPage + 1) * SLIDES_PER_SUMMARY_PAGE
  );
  const chartData: ChartData<'bar'> = {
    labels: visibleSlideCounts.map((slide) => slide.label),
    datasets: [
      {
        label: 'Include',
        data: visibleSlideCounts.map((slide) => slide.include),
        backgroundColor: theme.palette.success.main,
        borderRadius: 2,
        maxBarThickness: 18,
      },
      {
        label: 'Exclude',
        data: visibleSlideCounts.map((slide) => slide.exclude),
        backgroundColor: theme.palette.error.main,
        borderRadius: 2,
        maxBarThickness: 18,
      },
    ],
  };
  const chartOptions: ChartOptions<'bar'> = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'start',
        labels: { color: theme.palette.text.secondary },
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.primary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${context.parsed.x} ${context.parsed.x === 1 ? 'area' : 'areas'}`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { color: theme.palette.text.secondary, precision: 0 },
        grid: { color: theme.palette.divider },
        title: { display: true, text: 'Number of areas', color: theme.palette.text.secondary },
      },
      y: {
        ticks: { color: theme.palette.text.secondary },
        grid: { display: false },
      },
    },
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2">{assignment.name || ANALYSIS_AREAS_ASSIGNMENT_NAME}</Typography>
        <Chip size="small" variant="outlined" label={`Assignment ${assignment.annotationAssignmentId}`} />
        {assignment.archived ? <Chip size="small" label="Archived" /> : null}
        {annotationSlideIds.size === 0 ? <Chip size="small" variant="outlined" label="No saved areas yet" /> : null}
      </Stack>
      {visibleSlideCounts.length ? (
        <Stack spacing={1}>
          <Box>
            <Typography variant="subtitle2">
              Areas by slide · page {currentPage + 1} of {pageCount}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Counts include saved and draft areas.
            </Typography>
          </Box>
          <Box
            role="img"
            aria-label="Grouped bar chart comparing Include and Exclude areas for the slides on this page. Exact counts are in the table."
            sx={{ height: Math.max(220, visibleSlideCounts.length * 32), position: 'relative' }}
          >
            <Bar data={chartData} options={chartOptions} />
          </Box>
          <TableContainer>
            <Table size="small" aria-label="Area counts by slide">
              <TableHead>
                <TableRow>
                  <TableCell>Slide</TableCell>
                  <TableCell>Annotation status</TableCell>
                  <TableCell align="right">Include areas</TableCell>
                  <TableCell align="right">Exclude areas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleSlideCounts.map((slide, index) => (
                  <TableRow key={`${slide.slideId}-${index}`}>
                    <TableCell component="th" scope="row">
                      <SlideLinkWithPreview
                        slideid={slide.slideId}
                        labid={labId}
                        href={buildSlideViewerUrl({
                          slideId: slide.slideId,
                          studyId,
                          labId,
                          annotationAssignmentId: assignment.annotationAssignmentId,
                          reviewTab: ANALYSIS_AREAS_REVIEW_TAB,
                        })}
                      >
                        {slide.label}
                      </SlideLinkWithPreview>
                    </TableCell>
                    <TableCell>
                      {slide.workingState ? (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{ color: getAnnotationWorkingStateColor(theme, slide.workingState) }}
                        >
                          {React.cloneElement(AnnotationWorkingStatesIcons[slide.workingState], { fontSize: 'small' })}
                          <Typography variant="caption" sx={{ color: 'inherit' }}>
                            {getAnnotationWorkingStateLabel(slide.workingState)}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Not assigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{slide.include}</TableCell>
                    <TableCell align="right">{slide.exclude}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {slideCounts.length > SLIDES_PER_SUMMARY_PAGE ? (
            <TablePagination
              component="div"
              count={slideCounts.length}
              page={currentPage}
              rowsPerPage={SLIDES_PER_SUMMARY_PAGE}
              rowsPerPageOptions={[]}
              labelDisplayedRows={({ from, to, count }) => `Slides ${from}-${to} of ${count}`}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
            />
          ) : null}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No slides are available for this assignment.
        </Typography>
      )}
    </Stack>
  );
};

export const AnalysisAreasDialog: React.FC<AnalysisAreasDialogProps> = ({ studyId, labId, slides, onClose }) => {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(Permission.CreateAnnotationAssignment);
  const [createError, setCreateError] = useState<string | null>(null);
  const assignmentsQuery = useAnnotationAssignments(studyId, false, true);
  const assignments = (assignmentsQuery.data?.annotationAssignments || []).filter(isAnalysisAreaAssignment);
  const activeAssignments = assignments.filter((assignment) => !assignment.archived);
  const exploreAssignment = activeAssignments.length === 1 ? activeAssignments[0] : undefined;
  const exploreDatasetUrl =
    exploreAssignment && slides.length
      ? getAnalysisAreasDatasetUrl({
          studyId,
          labId,
          slideIds: slides.map((slide) => slide.id),
          annotationAssignmentId: exploreAssignment.annotationAssignmentId,
        })
      : undefined;
  const canCreateAssignment = canCreate && activeAssignments.length === 0 && slides.length > 0;

  const createAssignment = useMutationWithErrorSnackbar({
    mutationDescription: 'Create analysis-area assignment',
    mutationFn: createAnnotationAssignment,
    onError: (error) => {
      setCreateError(getQueryErrorMessage(error, 'The analysis-area assignment was not created.'));
    },
    onSuccess: async () => {
      setCreateError(null);
      enqueueSnackbar('Analysis-area assignment created.', { variant: 'success' });
      await queryClient.invalidateQueries([ANNOTATIONS_BASE_QUERY_KEY]);
    },
  });

  const createAnalysisAreas = () => {
    setCreateError(null);
    createAssignment.mutate({
      name: ANALYSIS_AREAS_ASSIGNMENT_NAME,
      isGlobal: false,
      todos: [analysisAreaTodo()],
      labId,
      studyId,
      filters: { studyId },
      slideIdsToInclude: slides.map((slide) => slide.id),
    });
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" aria-labelledby="analysis-areas-title">
      <DialogChromeHeader
        title="Analysis areas"
        titleId="analysis-areas-title"
        onClose={onClose}
        subtitle="Draw Include within the tissue segmentation mask to limit the analysis area. Without Include, the tissue segmentation mask is used by default. Exclude areas are then removed. Cells outside the resulting area are removed."
      />
      <DialogContent dividers>
        {assignmentsQuery.isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} aria-label="Loading analysis areas" />
            <Typography variant="body2" color="text.secondary">
              Loading analysis areas…
            </Typography>
          </Stack>
        ) : assignmentsQuery.isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" onClick={() => assignmentsQuery.refetch()}>
                Retry
              </Button>
            }
          >
            Analysis areas could not be loaded.
          </Alert>
        ) : (
          <Stack spacing={2}>
            {assignments.length > 1 ? (
              <Alert severity="info">
                More than one analysis-area assignment exists. Each one stays listed. A new assignment is not created
                while an active one is already present.
              </Alert>
            ) : null}
            {createError ? <Alert severity="error">{createError}</Alert> : null}
            {assignments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {!canCreate
                  ? 'Creating an analysis-area assignment requires annotation assignment permission.'
                  : slides.length
                  ? `Create one study assignment for ${slides.length} multiplex ${
                      slides.length === 1 ? 'slide' : 'slides'
                    }. Include focuses analysis. Exclude leaves areas out.`
                  : 'No multiplex slides are in this study yet.'}
              </Typography>
            ) : (
              assignments.map((assignment) => (
                <Stack key={assignment.annotationAssignmentId} spacing={1}>
                  {assignments.length > 1 ? <Chip size="small" variant="outlined" label="Duplicate" /> : null}
                  <AssignmentStatusSummary assignment={assignment} slides={slides} studyId={studyId} labId={labId} />
                </Stack>
              ))
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogChromeFooter>
        {exploreDatasetUrl ? (
          <Button component={RouterLink} to={exploreDatasetUrl} variant="outlined" color="secondary" onClick={onClose}>
            Explore dataset and annotate areas
          </Button>
        ) : null}
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Close
        </Button>
        {canCreate && activeAssignments.length === 0 && !assignmentsQuery.isLoading && !assignmentsQuery.isError ? (
          <Button
            variant="contained"
            color="primary"
            onClick={createAnalysisAreas}
            disabled={!canCreateAssignment || createAssignment.isLoading}
            startIcon={createAssignment.isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {createAssignment.isLoading ? 'Creating analysis-area assignment' : 'Create analysis-area assignment'}
          </Button>
        ) : null}
      </DialogChromeFooter>
    </Dialog>
  );
};
